import { useState } from "react";
import type { ReactNode } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Group,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useAuth } from "@/auth/useAuth";
import { listAppVersionPolicies } from "@/api/appVersionPolicies";
import { DetailGrid } from "@/components/DetailGrid";
import { JsonExpander } from "@/components/JsonExpander";
import { PageHeader } from "@/components/PageHeader";
import { RelatedLink } from "@/components/RelatedLink";
import { Timestamp } from "@/components/Timestamp";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { env } from "@/config/env";
import { COLLECTIONS } from "@/firestore/collections";
import { useFirestoreQuery } from "@/firestore/hooks";
import type {
  AppPlatform,
  AppVersionPolicyDoc,
  AppVersionPolicyRecord,
} from "@/firestore/appVersionPolicies";
import type { WithDocId } from "@/firestore/types";
import { isPermissionError } from "@/lib/errors";
import { AppVersionPolicyDialog } from "./app-versions/AppVersionPolicyDialog";
import {
  PLATFORMS,
  PLATFORM_LABELS,
  gateStatus,
  latestUpdatedAt,
  livePolicyFor,
} from "./app-versions/presentation";
import { useAppVersionPolicyCommand } from "./app-versions/useAppVersionPolicyCommand";

interface PlatformCardProps {
  platform: AppPlatform;
  doc: WithDocId<AppVersionPolicyDoc> | null;
  /** The admin `GET` row matching `doc`'s version, or null while it catches up / is unavailable. */
  record: AppVersionPolicyRecord | null;
  auditPending: boolean;
  canAct: boolean;
  now: number;
  onEdit(): void;
}

function PlatformCard({
  platform,
  doc,
  record,
  auditPending,
  canAct,
  now,
  onEdit,
}: PlatformCardProps) {
  const status = gateStatus(doc, now);
  const audit = (value: ReactNode) =>
    record ? value : <Text c="dimmed">{auditPending ? "Refreshing…" : "—"}</Text>;
  return (
    <Card withBorder radius="md" padding="lg">
      <Stack>
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Text fw={700} fz="lg">
              {PLATFORM_LABELS[platform]}
            </Text>
            <Badge color={status.color} variant="light" radius="sm" tt="none">
              {status.label}
            </Badge>
          </Group>
          <Button
            size="xs"
            variant={doc ? "default" : "filled"}
            disabled={!canAct}
            onClick={onEdit}
          >
            {doc ? "Edit" : "Create policy"}
          </Button>
        </Group>
        {doc ? (
          <>
            <DetailGrid
              cols={2}
              fields={[
                {
                  label: "Required build",
                  value: (
                    <Text fw={700} fz="xl">
                      {doc.requiredBuild}
                    </Text>
                  ),
                },
                { label: "Recommended build", value: String(doc.recommendedBuild) },
                {
                  label: "Required from",
                  value: doc.requiredEffectiveAt ? (
                    <Timestamp value={doc.requiredEffectiveAt} absolute />
                  ) : (
                    "Immediately"
                  ),
                },
                { label: "Updated", value: <Timestamp value={doc.updatedAt} absolute /> },
                {
                  label: "Updated by",
                  value: audit(<RelatedLink entity="user" id={record?.updatedBy} />),
                },
                { label: "Reason", value: audit(<Text>{record?.reason}</Text>), full: true },
                {
                  label: "Store link override",
                  value: doc.storeUrl ? (
                    <Anchor href={doc.storeUrl} target="_blank" rel="noreferrer" size="sm">
                      {doc.storeUrl}
                    </Anchor>
                  ) : (
                    <Text c="dimmed">— (compiled store link)</Text>
                  ),
                  full: true,
                },
                {
                  label: "Message shown to users",
                  value: doc.message ?? <Text c="dimmed">—</Text>,
                  full: true,
                },
              ]}
            />
            <JsonExpander value={doc} />
          </>
        ) : (
          <EmptyState
            title="No policy yet — create one"
            description="Start with required 0 / recommended 0: that changes nothing for users and lets you raise the floor later without a create."
          />
        )}
      </Stack>
    </Card>
  );
}

export function AppVersionsPage() {
  const { user, capabilities, getIdToken } = useAuth();
  const uid = user?.uid ?? "";
  const command = useAppVersionPolicyCommand();
  // Live values: the whole collection (≤ 2 docs), no filters → no composite index.
  const live = useFirestoreQuery<AppVersionPolicyDoc>(COLLECTIONS.appVersionPolicies);
  const revision = latestUpdatedAt(live.data);
  // Audit fields (`reason`, `updatedBy`) are not projected; refetch the admin GET whenever any
  // policy changes (AccountDeletionPage pattern).
  const records = useQuery({
    queryKey: ["app-version-policies", env.firebase.projectId, uid, revision],
    queryFn: async ({ signal }) => listAppVersionPolicies(await getIdToken(true), signal),
    enabled: !!uid,
    retry: false,
    gcTime: 0,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const [dialog, setDialog] = useState<{ platform: AppPlatform; version: string | null } | null>(
    null,
  );
  const now = Date.now();

  const crumbs = [{ label: "Platform" }, { label: "App versions" }];
  const dialogDoc = dialog ? livePolicyFor(live.data, dialog.platform) : null;

  return (
    <>
      <PageHeader
        title="App versions"
        subtitle="Minimum supported app build per platform. Builds below the required floor get a full-screen update gate and every backend command answers 426."
        crumbs={crumbs}
        actions={
          <Button
            variant="default"
            size="xs"
            disabled={command.pending}
            onClick={() => {
              live.refresh();
              void records.refetch();
            }}
          >
            Refresh
          </Button>
        }
      />
      <Stack gap="lg">
        {env.isProd ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            Production. Raising a required floor blocks real users within seconds. Never set it
            above a build that 100 % of that platform can download today; lower it to 0 first if
            anything looks wrong.
          </Alert>
        ) : null}
        {live.error ? (
          isPermissionError(live.error) ? (
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              title="Access denied"
            >
              The <Code>appVersionPolicies</Code> collection could not be read. Its Firestore rules
              may not be deployed in this environment yet — they ship with the identity backend
              release that adds the app version gate. If you were just granted admin, sign out and
              back in.
            </Alert>
          ) : (
            <ErrorState error={live.error} />
          )
        ) : live.loading && live.data.length === 0 ? (
          <LoadingState />
        ) : (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {PLATFORMS.map((platform) => {
              const doc = livePolicyFor(live.data, platform);
              const match = doc ? (records.data?.find((r) => r.id === doc.id) ?? null) : null;
              return (
                <PlatformCard
                  key={platform}
                  platform={platform}
                  doc={doc}
                  record={match && doc && match.updatedAt === doc.updatedAt ? match : null}
                  auditPending={records.isFetching || records.isPending}
                  canAct={command.canAct}
                  now={now}
                  onEdit={() => setDialog({ platform, version: doc?.updatedAt ?? null })}
                />
              );
            })}
          </SimpleGrid>
        )}
        {records.error ? (
          <Alert color="orange" variant="light">
            Audit fields (reason, updated by) could not be loaded:{" "}
            {records.error instanceof Error ? records.error.message : String(records.error)}
          </Alert>
        ) : null}
      </Stack>
      {dialog ? (
        <AppVersionPolicyDialog
          key={`${dialog.platform}:${dialog.version ?? "create"}`}
          platform={dialog.platform}
          current={dialogDoc}
          expectedUpdatedAt={dialog.version}
          capabilities={capabilities}
          pending={command.pending}
          onSubmit={async (cmd) => {
            await command.execute(cmd);
            notifications.show({
              color: "teal",
              title: "Policy saved",
              message: "The API picks it up within about a minute; devices within seconds.",
            });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
